import SUBUSER_PERMISSION_SLUGS, {
  canonicalizePermissionSlug,
  getPermissionSlug,
} from '@/constants/authorization/subuser-permission-slugs'

type SubjectSlugs = {
  view: string;
  manage: string;
  delete?: string;
  export?: string;
  assign?: string;
}

function crudMap(subjects: Record<string, SubjectSlugs>): Record<string, string> {
  const map: Record<string, string> = {}
  Object.entries(subjects).forEach(([subject, slugs]) => {
    map[`view:${subject}`] = slugs.view
    map[`manage:${subject}`] = slugs.manage
    map[`add:${subject}`] = slugs.manage
    map[`edit:${subject}`] = slugs.manage
    map[`delete:${subject}`] = slugs.delete ?? slugs.manage
    if (slugs.export) map[`export:${subject}`] = slugs.export
    if (slugs.assign) map[`assign:${subject}`] = slugs.assign
  })
  return map
}

const ACTION_SUBJECT_SLUG_MAP: Record<string, string> = {
  ...crudMap({
    Order: {
      view: SUBUSER_PERMISSION_SLUGS.ORDERS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.ORDERS_MANAGE,
      export: SUBUSER_PERMISSION_SLUGS.ORDERS_EXPORT,
    },
    Role: {
      view: SUBUSER_PERMISSION_SLUGS.ROLES_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.ROLES_MANAGE,
      assign: SUBUSER_PERMISSION_SLUGS.ROLES_ASSIGN,
    },
    Subuser: {
      view: SUBUSER_PERMISSION_SLUGS.SUBUSERS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SUBUSERS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.SUBUSERS_DELETE,
    },
    MenuItem: {
      view: SUBUSER_PERMISSION_SLUGS.MENU_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.MENU_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.MENU_DELETE,
    },
    MenuCategory: {
      view: SUBUSER_PERMISSION_SLUGS.MENU_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.MENU_CATEGORIES_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.MENU_CATEGORY_DELETE,
    },
    Menu: {
      view: SUBUSER_PERMISSION_SLUGS.MENU_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.MENU_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.MENU_DELETE,
    },
    MenuBulk: {
      view: SUBUSER_PERMISSION_SLUGS.MENU_BULK_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.MENU_BULK_MANAGE,
    },
    Modifier: {
      view: SUBUSER_PERMISSION_SLUGS.MODIFIERS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.MODIFIERS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.MODIFIERS_DELETE,
    },
    Tax: {
      view: SUBUSER_PERMISSION_SLUGS.TAXES_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.TAXES_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.TAXES_DELETE,
    },
    TaxGroup: {
      view: SUBUSER_PERMISSION_SLUGS.TAX_GROUPS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.TAX_GROUPS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.TAX_GROUPS_DELETE,
    },
    Client: {
      view: SUBUSER_PERMISSION_SLUGS.CLIENTS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.CLIENTS_MANAGE,
      export: SUBUSER_PERMISSION_SLUGS.CLIENTS_EXPORT,
    },
    Customer: {
      view: SUBUSER_PERMISSION_SLUGS.CLIENTS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.CLIENTS_MANAGE,
      export: SUBUSER_PERMISSION_SLUGS.CLIENTS_EXPORT,
    },
    InventoryItem: {
      view: SUBUSER_PERMISSION_SLUGS.INVENTORY_INGREDIENTS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.INVENTORY_INGREDIENTS_MANAGE,
    },
    InventoryIngredient: {
      view: SUBUSER_PERMISSION_SLUGS.INVENTORY_INGREDIENTS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.INVENTORY_INGREDIENTS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.INVENTORY_INGREDIENTS_DELETE,
    },
    InventoryRecipe: {
      view: SUBUSER_PERMISSION_SLUGS.INVENTORY_RECIPES_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.INVENTORY_RECIPES_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.INVENTORY_RECIPES_DELETE,
    },
    InventoryUnit: {
      view: SUBUSER_PERMISSION_SLUGS.INVENTORY_UNITS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.INVENTORY_UNITS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.INVENTORY_UNITS_DELETE,
    },
    InventoryTransaction: {
      view: SUBUSER_PERMISSION_SLUGS.INVENTORY_TRANSACTIONS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.INVENTORY_TRANSACTIONS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.INVENTORY_TRANSACTIONS_DELETE,
    },
    InventorySupplier: {
      view: SUBUSER_PERMISSION_SLUGS.INVENTORY_SUPPLIERS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.INVENTORY_SUPPLIERS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.INVENTORY_SUPPLIERS_DELETE,
    },
    InventoryCount: {
      view: SUBUSER_PERMISSION_SLUGS.INVENTORY_COUNTS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.INVENTORY_COUNTS_MANAGE,
    },
    SpotCheck: {
      view: SUBUSER_PERMISSION_SLUGS.INVENTORY_SPOT_CHECKS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.INVENTORY_SPOT_CHECKS_MANAGE,
    },
    Report: {
      view: SUBUSER_PERMISSION_SLUGS.REPORTS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.REPORTS_VIEW,
      export: SUBUSER_PERMISSION_SLUGS.REPORTS_EXPORT,
    },
    Pos: {
      view: SUBUSER_PERMISSION_SLUGS.POS_ACCESS,
      manage: SUBUSER_PERMISSION_SLUGS.POS_PREVIEW_VIEW,
    },
    PosSession: {
      view: SUBUSER_PERMISSION_SLUGS.POS_SESSIONS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.POS_SESSIONS_MANAGE,
    },
    PosTerminal: {
      view: SUBUSER_PERMISSION_SLUGS.POS_TERMINALS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.POS_TERMINALS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.POS_TERMINALS_DELETE,
    },
    PosSetting: {
      view: SUBUSER_PERMISSION_SLUGS.POS_SETTINGS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.POS_SETTINGS_MANAGE,
    },
    PosPaymentMethod: {
      view: SUBUSER_PERMISSION_SLUGS.POS_PAYMENT_METHODS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.POS_PAYMENT_METHODS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.POS_PAYMENT_METHODS_DELETE,
    },
    Promotion: {
      view: SUBUSER_PERMISSION_SLUGS.PROMOTIONS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.PROMOTIONS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.PROMOTIONS_DELETE,
    },
    Coupon: {
      view: SUBUSER_PERMISSION_SLUGS.COUPONS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.COUPONS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.COUPONS_DELETE,
    },
    Discount: {
      view: SUBUSER_PERMISSION_SLUGS.DISCOUNTS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.DISCOUNTS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.DISCOUNTS_DELETE,
    },
    Banner: {
      view: SUBUSER_PERMISSION_SLUGS.BANNERS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.BANNERS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.BANNERS_DELETE,
    },
    Survey: {
      view: SUBUSER_PERMISSION_SLUGS.SURVEYS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SURVEYS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.SURVEYS_DELETE,
    },
    Newsletter: {
      view: SUBUSER_PERMISSION_SLUGS.NEWSLETTERS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.NEWSLETTERS_MANAGE,
    },
    Campaign: {
      view: SUBUSER_PERMISSION_SLUGS.CAMPAIGNS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.CAMPAIGNS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.MARKETING_CAMPAIGNS_DELETE,
    },
    Wallet: {
      view: SUBUSER_PERMISSION_SLUGS.WALLETS_MANAGE,
      manage: SUBUSER_PERMISSION_SLUGS.WALLETS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.WALLETS_DELETE,
    },
    MarketingEvent: {
      view: SUBUSER_PERMISSION_SLUGS.MARKETING_EVENTS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.MARKETING_EVENTS_MANAGE,
    },
    CustomerTag: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_TAGS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_TAGS_MANAGE,
    },
    Tier: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_TIERS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_TIERS_MANAGE,
    },
    BranchSetting: {
      view: SUBUSER_PERMISSION_SLUGS.BRANCHES_SETTINGS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.BRANCHES_SETTINGS_MANAGE,
    },
    SettingsBusinessInfo: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_BUSINESS_INFO_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_BUSINESS_INFO_MANAGE,
    },
    SettingsBusinessSettings: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_BUSINESS_SETTINGS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_BUSINESS_SETTINGS_MANAGE,
    },
    SettingsSocialLinks: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_SOCIAL_LINKS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_SOCIAL_LINKS_MANAGE,
    },
    SettingsWhatsappReport: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_WHATSAPP_REPORTS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_WHATSAPP_REPORTS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.SETTINGS_WHATSAPP_REPORTS_DELETE,
    },
    SettingsEmailReport: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_EMAIL_REPORTS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_EMAIL_REPORTS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.SETTINGS_EMAIL_REPORTS_DELETE,
    },
    SettingsPolicy: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_POLICIES_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_POLICIES_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.SETTINGS_POLICIES_DELETE,
    },
    SettingsTier: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_TIERS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_TIERS_MANAGE,
    },
    SettingsTag: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_TAGS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_TAGS_MANAGE,
    },
    SettingsAgeGroup: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_AGE_GROUPS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_AGE_GROUPS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.SETTINGS_AGE_GROUPS_DELETE,
    },
    ReturnReason: {
      view: SUBUSER_PERMISSION_SLUGS.POS_RETURN_REASONS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.POS_RETURN_REASONS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.POS_RETURN_REASONS_DELETE,
    },
    CancelReason: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_CANCEL_REASONS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_CANCEL_REASONS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.SETTINGS_CANCEL_REASONS_DELETE,
    },
    Branch: {
      view: SUBUSER_PERMISSION_SLUGS.BRANCHES_MANAGE,
      manage: SUBUSER_PERMISSION_SLUGS.BRANCHES_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.BRANCHES_DELETE,
    },
    Integration: {
      view: SUBUSER_PERMISSION_SLUGS.INTEGRATIONS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.INTEGRATIONS_CONNECTIONS_MANAGE,
    },
    Reservation: {
      view: SUBUSER_PERMISSION_SLUGS.RESERVATIONS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.RESERVATIONS_MANAGE,
    },
    Queue: {
      view: SUBUSER_PERMISSION_SLUGS.QUEUES_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.QUEUES_MANAGE,
    },
    Kitchen: {
      view: SUBUSER_PERMISSION_SLUGS.KITCHEN_ACCESS,
      manage: SUBUSER_PERMISSION_SLUGS.KITCHEN_SETTINGS_MANAGE,
    },
    Ods: {
      view: SUBUSER_PERMISSION_SLUGS.ODS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.ODS_MANAGE,
    },
    Media: {
      view: SUBUSER_PERMISSION_SLUGS.MEDIA_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.MEDIA_MANAGE,
    },
    Notification: {
      view: SUBUSER_PERMISSION_SLUGS.NOTIFICATIONS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.NOTIFICATIONS_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.NOTIFICATIONS_DELETE,
    },
    PaymentLink: {
      view: SUBUSER_PERMISSION_SLUGS.PAYMENT_LINKS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.PAYMENT_LINKS_MANAGE,
    },
    Review: {
      view: SUBUSER_PERMISSION_SLUGS.REVIEWS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.REVIEWS_MANAGE,
    },
    ActivityLog: {
      view: SUBUSER_PERMISSION_SLUGS.AUDITOR_ACTIVITY_LOGS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.AUDITOR_ACTIVITY_LOGS_VIEW,
    },
    Device: {
      view: SUBUSER_PERMISSION_SLUGS.GENERAL_DEVICES_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.GENERAL_DEVICES_MANAGE,
    },
    DisplayDevice: {
      view: SUBUSER_PERMISSION_SLUGS.DISPLAY_DEVICES_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.DISPLAY_DEVICES_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.DISPLAY_DEVICES_DELETE,
    },
    Transaction: {
      view: SUBUSER_PERMISSION_SLUGS.TRANSACTIONS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.TRANSACTIONS_MANAGE,
    },
    Setting: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_BUSINESS_INFO_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_BUSINESS_INFO_MANAGE,
    },
    Theme: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_THEMES_MANAGE,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_THEMES_MANAGE,
    },
    QrCode: {
      view: SUBUSER_PERMISSION_SLUGS.GENERAL_QR_MANAGE,
      manage: SUBUSER_PERMISSION_SLUGS.GENERAL_QR_MANAGE,
    },
    Receipt: {
      view: SUBUSER_PERMISSION_SLUGS.BRANCHES_RECEIPT_MANAGE,
      manage: SUBUSER_PERMISSION_SLUGS.BRANCHES_RECEIPT_MANAGE,
    },
    Statistics: {
      view: SUBUSER_PERMISSION_SLUGS.GENERAL_STATISTICS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.GENERAL_STATISTICS_VIEW,
    },
    Policy: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_POLICIES_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_POLICIES_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.SETTINGS_POLICIES_DELETE,
    },
    ReturnOrder: {
      view: SUBUSER_PERMISSION_SLUGS.ORDERS_RETURN_ORDERS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.POS_RETURN_ORDERS_MANAGE,
    },
    AbandonedOrder: {
      view: SUBUSER_PERMISSION_SLUGS.ORDERS_ABANDONED_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.ORDERS_ABANDONED_MANAGE,
    },
    ReservationOrder: {
      view: SUBUSER_PERMISSION_SLUGS.ORDERS_RESERVATION_ORDERS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.ORDERS_RESERVATION_ORDERS_MANAGE,
    },
    Marketing: {
      view: SUBUSER_PERMISSION_SLUGS.MARKETING_ACCESS,
      manage: SUBUSER_PERMISSION_SLUGS.MARKETING_ACCESS,
    },
    OrderDisplay: {
      view: SUBUSER_PERMISSION_SLUGS.ORDER_DISPLAY_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.ORDER_DISPLAY_MANAGE,
    },
    SalesReport: {
      view: SUBUSER_PERMISSION_SLUGS.REPORTS_SALES_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.REPORTS_SALES_VIEW,
      export: SUBUSER_PERMISSION_SLUGS.REPORTS_EXPORT,
    },
    GainAndLossReport: {
      view: SUBUSER_PERMISSION_SLUGS.REPORTS_GAIN_AND_LOSS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.REPORTS_GAIN_AND_LOSS_VIEW,
      export: SUBUSER_PERMISSION_SLUGS.REPORTS_EXPORT,
    },
    SalesPerReport: {
      view: SUBUSER_PERMISSION_SLUGS.REPORTS_SALES_PER_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.REPORTS_SALES_PER_VIEW,
      export: SUBUSER_PERMISSION_SLUGS.REPORTS_EXPORT,
    },
    OrderLinesReport: {
      view: SUBUSER_PERMISSION_SLUGS.REPORTS_ORDER_LINES_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.REPORTS_ORDER_LINES_VIEW,
      export: SUBUSER_PERMISSION_SLUGS.REPORTS_EXPORT,
    },
    TransferableOrdersReport: {
      view: SUBUSER_PERMISSION_SLUGS.REPORTS_TRANSFERABLE_ORDERS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.REPORTS_TRANSFERABLE_ORDERS_VIEW,
      export: SUBUSER_PERMISSION_SLUGS.REPORTS_EXPORT,
    },
    BranchesStatisticsReport: {
      view: SUBUSER_PERMISSION_SLUGS.REPORTS_BRANCHES_STATISTICS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.REPORTS_BRANCHES_STATISTICS_VIEW,
      export: SUBUSER_PERMISSION_SLUGS.REPORTS_EXPORT,
    },
    InventoryStock: {
      view: SUBUSER_PERMISSION_SLUGS.INVENTORY_STOCK_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.INVENTORY_STOCK_VIEW,
    },
    InventoryStockReport: {
      view: SUBUSER_PERMISSION_SLUGS.INVENTORY_STOCK_REPORT_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.INVENTORY_STOCK_REPORT_VIEW,
    },
    MarketingTag: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_TAGS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_TAGS_MANAGE,
    },
    MarketingTier: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_TIERS_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_TIERS_MANAGE,
    },
    GeneralPolicy: {
      view: SUBUSER_PERMISSION_SLUGS.SETTINGS_POLICIES_VIEW,
      manage: SUBUSER_PERMISSION_SLUGS.SETTINGS_POLICIES_MANAGE,
      delete: SUBUSER_PERMISSION_SLUGS.SETTINGS_POLICIES_DELETE,
    },
    Feedback: {
      view: SUBUSER_PERMISSION_SLUGS.GENERAL_FEEDBACK_SEND,
      manage: SUBUSER_PERMISSION_SLUGS.GENERAL_FEEDBACK_SEND,
    },
  }),

  'send:Feedback': SUBUSER_PERMISSION_SLUGS.GENERAL_FEEDBACK_SEND,
  'upload:Media': SUBUSER_PERMISSION_SLUGS.MEDIA_UPLOAD,
  'create:OrderPayment': SUBUSER_PERMISSION_SLUGS.ORDERS_PAYMENT_CREATE,
  'view:OrderNotification': SUBUSER_PERMISSION_SLUGS.ORDERS_NOTIFICATIONS_VIEW,
  'manage:OrderTransfer': SUBUSER_PERMISSION_SLUGS.ORDERS_TRANSFER_MANAGE,
  'submit:InventoryCount': SUBUSER_PERMISSION_SLUGS.INVENTORY_COUNTS_SUBMIT,
  'submit:SpotCheck': SUBUSER_PERMISSION_SLUGS.INVENTORY_SPOT_CHECKS_SUBMIT,
  'assign:Order': SUBUSER_PERMISSION_SLUGS.ORDERS_TRANSFER_EXECUTE,
  'validate:Order': SUBUSER_PERMISSION_SLUGS.ORDERS_TRANSFER_VALIDATE,
  'refund:Order': SUBUSER_PERMISSION_SLUGS.ORDERS_REFUND,
  'push:Order': SUBUSER_PERMISSION_SLUGS.KITCHEN_ORDERS_PUSH,
  'push:Kitchen': SUBUSER_PERMISSION_SLUGS.KITCHEN_ORDERS_PUSH,
  'sync:Menu': SUBUSER_PERMISSION_SLUGS.MENU_SYNC,
  'busy:Ods': SUBUSER_PERMISSION_SLUGS.ODS_BUSY,
  'manage:PosFloor': SUBUSER_PERMISSION_SLUGS.POS_FLOORS_MANAGE,
  'view:PosFloor': SUBUSER_PERMISSION_SLUGS.POS_FLOORS_MANAGE,
}

export function resolveSubuserPermissionSlug(action: string, subject?: string): string | null {
  if (!action) return null
  if (!subject && action.includes('.')) return action
  if (!subject) return null
  return ACTION_SUBJECT_SLUG_MAP[`${action}:${subject}`] ?? null
}

function toPermissionSlug(permission: unknown): string | null {
  if (typeof permission === 'string' && permission.includes('.')) {
    return canonicalizePermissionSlug(permission)
  }
  if (permission && typeof permission === 'object') {
    return getPermissionSlug(permission as { slug?: string })
  }
  return null
}

export function normalizeSubuserPermissions(payload: any = {}) {
  const rawPermissions = Array.isArray(payload)
    ? payload
    : (payload?.permissions ?? [])

  const permissions = rawPermissions
    .map(toPermissionSlug)
    .filter(Boolean) as string[]

  return {
    roles: Array.isArray(payload) ? [] : (payload?.roles ?? []),
    permissions,
  }
}

export default resolveSubuserPermissionSlug
